/**
 * AdventureBanner Component
 * 
 * Displays an adventure banner with WebP/JPEG fallback support and lazy loading.
 * Used in scene headers during gameplay alongside character portraits.
 * Aspect ratio: 5:1 (ultra-wide panoramic)
 */

import React, { useState } from 'react';
import { imageService } from '../../services/imageService';
import type { ImageVariantSize, ImageUrls } from '../../services/imageService';

export interface AdventureBannerProps {
  /**
   * Banner ID to display (e.g., 'fantasy', 'mystery', 'generic')
   * If undefined or not found, falls back to 'generic'
   */
  bannerId?: string | undefined;
  
  /**
   * Size variant to display
   * - thumbnail: 320×64px (for scene headers)
   * - card: 640×128px (for medium displays)
   * - large: 1280×256px (for large displays)
   */
  variant?: ImageVariantSize;
  
  /**
   * Alt text for the image (for accessibility)
   */
  alt?: string;
  
  /**
   * Additional CSS classes
   */
  className?: string;
  
  /**
   * Inline styles
   */
  style?: React.CSSProperties;
  
  /**
   * Custom fallback element to display if image fails to load
   */
  fallback?: React.ReactNode;
  
  /**
   * Whether to use lazy loading (default: true)
   */
  lazy?: boolean;
}

/**
 * Default fallback component when banner cannot be loaded
 */
const DefaultFallback: React.FC<{ variant: ImageVariantSize; alt: string | undefined }> = ({ variant, alt }) => {
  const sizes = {
    thumbnail: 'w-80 h-16',     // 320×64 equivalent (5:1)
    card: 'w-[40rem] h-32',      // 640×128 equivalent (5:1)
    large: 'w-[80rem] h-64',     // 1280×256 equivalent (5:1)
  };

  return (
    <div 
      className={`${sizes[variant]} flex items-center justify-center bg-gray-700 text-gray-400 overflow-hidden`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)'
      }}
      role="img"
      aria-label={alt || 'Adventure banner placeholder'}
    >
      <span className="text-2xl">━━━━━━━━━━━</span>
    </div>
  );
};

/**
 * AdventureBanner component
 */
export const AdventureBanner: React.FC<AdventureBannerProps> = ({
  bannerId,
  variant = 'thumbnail',
  alt,
  className = '',
  style,
  fallback,
  lazy = true,
}) => {
  // Track which URL failed (not just a boolean)
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Effect to wait for image IDs to load
  React.useEffect(() => {
    let isMounted = true;
    
    if (!imageService.isLoaded()) {
      imageService.loadAvailableIds().then(() => {
        // IDs loaded successfully
        if (isMounted) {
          // Force a re-render by updating a state
          setIsLoading(false);
        }
      }).catch(() => {
        // Silently handle errors - images will fall back to placeholders
      });
    }
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Get banner URLs from the service
  const urls: ImageUrls = imageService.getBannerUrls(bannerId, variant);

  // If THIS specific URL failed, show fallback
  if (failedUrl === urls.jpg) {
    return fallback ? (
      <>{fallback}</>
    ) : (
      <DefaultFallback variant={variant} alt={alt} />
    );
  }

  // Size classes for the container (5:1 aspect ratio)
  const sizeClasses = {
    thumbnail: 'w-80 h-16',     // 320×64 equivalent
    card: 'w-[40rem] h-32',      // 640×128 equivalent
    large: 'w-[80rem] h-64',     // 1280×256 equivalent
  };

  // Handle image load error
  const handleError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const failedSrc = e.currentTarget.src;
    console.warn(`Failed to load banner: ${bannerId}`);
    setFailedUrl(failedSrc);
    setIsLoading(false);
  };

  // Handle image load success
  const handleLoad = () => {
    setIsLoading(false);
  };

  // Use URLs as key to force remount when they change
  const imageKey = `${urls.webp}-${urls.jpg}`;

  return (
    <div 
      className={`${sizeClasses[variant]} relative ${className}`}
      style={{
        borderRadius: '12px',
        border: '1px solid #000000',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.6), 0 10px 25px -5px rgba(0, 0, 0, 0.3)',
        overflow: 'hidden',
        backgroundColor: 'transparent',
        ...style
      }}
    >
      {/* Show placeholder while loading */}
      {isLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-700 animate-pulse">
          <span className="text-gray-500 text-xs">Loading...</span>
        </div>
      )}
      
      {/* Picture element with WebP and JPEG sources */}
      {/* Using key prop to force remount when URLs change, clearing error state */}
      <picture key={imageKey} style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* WebP source (modern browsers) */}
        <source
          type="image/webp"
          srcSet={urls.webp}
        />
        
        {/* JPEG fallback (all browsers) */}
        <img
          src={urls.jpg}
          alt={alt || 'Adventure banner'}
          loading={lazy ? 'lazy' : 'eager'}
          onError={handleError}
          onLoad={handleLoad}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            display: 'block'
          }}
          className={`${isLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
        />
      </picture>
    </div>
  );
};

export default AdventureBanner;

